// Bits shared by the Vendor Cafe screens (Stephanie 2026-10-30): the invoice
// shape from GET /vendor-cafe/invoices, the exact text to type into Vendor
// Cafe, and a small "Copy" button for each field.
import { useState } from "react";
import { Check, Copy } from "lucide-react";

export interface VendorCafeInvoice {
  id: string;
  invoiceNumber: number;
  invoiceDate: string;
  total: string;
  status: string;
  clientId: string;
  clientName: string;
  projectId: string;
  property: string;
  propertyOverride: string | null;
  buildingProperty: string;
  proofsApproved: boolean;
  uploadedAt: string | null;
  uploadedByName: string | null;
  publicShareUuid: string;
  bucket: "ready" | "waiting" | "uploaded";
}

/** "2026-10-30" → "10/30/2026", the way it's typed into Vendor Cafe. */
export function vcDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return y && m && d ? `${m}/${d}/${y}` : iso;
}

/** The server's total, as typed: "1234.50" (no $ or commas). */
export function vcAmount(total: string): string {
  const n = Number(total);
  return Number.isFinite(n) ? n.toFixed(2) : total;
}

export function vcMoney(total: string): string {
  const n = Number(total);
  return Number.isFinite(n) ? n.toLocaleString("en-US", { style: "currency", currency: "USD" }) : total;
}

export function vcUploadedLine(i: Pick<VendorCafeInvoice, "uploadedAt" | "uploadedByName">): string {
  if (!i.uploadedAt) return "";
  const when = new Date(i.uploadedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return `Uploaded ${when}${i.uploadedByName ? ` by ${i.uploadedByName}` : ""}`;
}

/** The invoice the client sees, with its "Download as PDF" button. */
export function invoicePdfHref(publicShareUuid: string): string {
  return `/i/${publicShareUuid}`;
}

export function CopyValue({ value, label, shown }: { value: string; label: string; shown?: string }) {
  const [done, setDone] = useState(false);
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{shown ?? value}</span>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(value).then(() => {
            setDone(true);
            window.setTimeout(() => setDone(false), 1500);
          });
        }}
        className="rounded p-0.5 text-muted hover:bg-cream-100 hover:text-ink"
        aria-label={`Copy ${label}`}
        title={`Copy ${label}`}
      >
        {done ? <Check className="h-3.5 w-3.5 text-ink" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
      </button>
    </span>
  );
}
